import { EmptyState, ErrorState, FreshnessBar } from "../components/AdminPageStates";
import { useFounderStory } from "../hooks/useFounderStory";
import { FounderStoryEvent } from "../services/founderStoryService";

function formatStoryTime(value: Date) {
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  }).format(value);
}

function StoryEventRow({ event }: { event: FounderStoryEvent }) {
  return (
    <article className={`founder-story-row ${event.kind}`}>
      <span className="founder-story-icon" aria-hidden="true">
        {event.icon}
      </span>
      <div>
        <h3>{event.title}</h3>
        <p>{event.detail}</p>
        <small>
          {formatStoryTime(event.occurredAt)} · {event.source}
        </small>
      </div>
    </article>
  );
}

export default function FounderStory() {
  const { events, error, isLoading, lastUpdated } = useFounderStory();

  if (error) {
    return <ErrorState message="Unable to load Founder Story. Check Firebase permissions and realtime listener access." />;
  }

  if (!isLoading && events.length === 0) {
    return <EmptyState message="Chor's story will appear as people join, publish, book, submit, and create." />;
  }

  return (
    <section className="founder-story-page" aria-label="Founder Story">
      <FreshnessBar
        dataSource="Live read-only listeners from users, classSessions, events, submissions, bookings, marketplace_transactions, and milestone aggregates"
        isLive={true}
        lastUpdated={lastUpdated}
        onRefresh={() => undefined}
      />

      <header className="founder-story-hero">
        <p className="eyebrow">Founder Story</p>
        <h2>Watch Chor grow in real time.</h2>
        <p>
          A living timeline of the people, services, events, submissions, bookings, and revenue moments that turn Chor
          into a company.
        </p>
      </header>

      <section className="founder-story-timeline" aria-label="Chor company timeline">
        {isLoading
          ? Array.from({ length: 6 }, (_, index) => (
              <article className="founder-story-row loading-card" key={index} aria-busy="true">
                <span className="founder-story-icon" aria-hidden="true">
                  ·
                </span>
                <div>
                  <div className="skeleton skeleton-title" />
                  <div className="skeleton skeleton-note" />
                </div>
              </article>
            ))
          : events.map((event) => <StoryEventRow key={event.id} event={event} />)}
      </section>
    </section>
  );
}
